import React, { useState, useRef } from 'react'
import { Attachment } from 'stream-chat-react'
import { AppAvatar } from '../../../Common/AppAvatar/AppAvatar.component'
import { getInitials } from '../../helper'
import { formatMessageWithMentions } from '../../helper/messageFormatter'
import { ReactComponent as StarIconSvg } from '../../../../assets/svgs/star-icon.svg'
import { ReactComponent as ThreeDotsIconSvg } from '../../../../assets/svgs/three-dots-icon.svg'

// Three Dots Icon Component
const ThreeDotsIcon = () => (
  <ThreeDotsIconSvg className="w-4 h-4 text-app-grey-60" />
)

// Star Icon Component wrapper to handle filled state
const StarIcon = ({ filled = false }) => (
  <StarIconSvg 
    className={`w-5 h-5 ${filled ? "text-yellow-500" : "text-app-grey-65"}`}
    style={{
      stroke: filled ? "none" : "currentColor",
      fill: filled ? "currentColor" : "none",
      strokeWidth: filled ? "0" : "1.25"
    }}
  />
)

// Message Actions Dropdown Menu for starred messages
const StarredMessageActionsMenu = ({ 
  isVisible, 
  onUnstar,
  message
}) => {
  if (!isVisible) return null

  const menuItems = [
    { 
      icon: <StarIcon filled={true} />, 
      label: 'Remove star', 
      action: onUnstar 
    },
  ]

  return (
    <div className="absolute right-0 top-8 bg-app-white-100 rounded-lg shadow-lg border border-app-grey-25 py-2 min-w-[160px] z-50">
      {menuItems.map((item, index) => (
        <button
          key={index}
          onClick={item.action}
          className="w-full px-4 py-2 text-left text-sm text-app-grey-70 hover:bg-app-grey-5 flex items-center gap-3"
        >
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">{item.icon}</span>
          <span className="text-app-grey-100">{item.label}</span>
        </button>
      ))}
    </div>
  )
}

// Helper function to parse quoted message from text
const parseQuotedMessage = (text: string): { 
  isReply: true; 
  quotedUser: string; 
  quotedText: string; 
  replyText: string 
} | { 
  isReply: false; 
  originalText: string 
} => {
  const quotedPattern = /^> (.+?):\s(.+?)\n\n(.*)$/s
  const match = text.match(quotedPattern)
  
  if (match) {
    return {
      isReply: true,
      quotedUser: match[1],
      quotedText: match[2],
      replyText: match[3].trim()
    }
  }
  
  return {
    isReply: false,
    originalText: text
  }
}

// Custom Message Text component that formats mentions and handles replies
const StarredMessageText = ({ message }) => {
  const hasText = message.text && message.text.trim().length > 0
  const hasAttachments = message.attachments && message.attachments.length > 0

  // If no text and no attachments, return empty div
  if (!hasText && !hasAttachments) {
    return <div className="px-4 py-2 text-sm"></div>
  }

  // Parse the message to check if it's a reply
  const parsedMessage = hasText ? parseQuotedMessage(message.text) : { isReply: false }

  return (
    <div className="px-2 py-2 text-sm text-left">
      {/* Render reply structure if it's a reply */}
      {hasText && parsedMessage.isReply && 'quotedUser' in parsedMessage && (
        <div className="mb-2">
          {/* Quoted message */}
          <div className="mb-3 p-3 bg-app-grey-10 border-l-4 border-app-primary-60 rounded-r-lg">
            <div className="flex items-center gap-1 mb-1">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-app-grey-60">
                <path d="M3.5 7L7 3.5L8.06066 4.56066L6.12132 6.5H12.5C13.3284 6.5 14 7.17157 14 8V12H12.5V8H6.12132L8.06066 9.93934L7 11L3.5 7.5V7Z" fill="currentColor"/>
              </svg>
              <div className="text-xs text-app-grey-70 font-medium">
                {parsedMessage.quotedUser}
              </div>
            </div>
            <div className="text-sm text-app-grey-90 line-clamp-2">
              {formatMessageWithMentions(parsedMessage.quotedText)}
            </div>
          </div>
          
          {/* Reply text */}
          {parsedMessage.replyText && (
            <div className="text-left text-app-black-100">
              {formatMessageWithMentions(parsedMessage.replyText)}
            </div>
          )}
        </div>
      )}

      {/* Render normal text content if not a reply */}
      {hasText && !parsedMessage.isReply && (
        <div className="mb-2 text-left">
          {typeof message.text === 'string'
            ? formatMessageWithMentions(message.text)
            : message.text}
        </div>
      )}

      {/* Render attachments */}
      {hasAttachments && (
        <div className={hasText ? 'mt-2' : ''}>
          <Attachment attachments={message.attachments} />
        </div>
      )}
    </div>
  )
}

interface StarredMessageItemProps {
  message: any
  client: any
  onUnstar: (messageId: string) => Promise<void>
}

export const StarredMessageItem: React.FC<StarredMessageItemProps> = ({
  message,
  client,
  onUnstar
}) => {
  const [isHovering, setIsHovering] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const messageRef = useRef<HTMLDivElement>(null)

  const isMyMessage = () => {
    return client?.userID === message.user?.id
  }

  const handleMouseEnter = () => {
    setIsHovering(true)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
    setShowActionsMenu(false)
  }

  const handleThreeDotsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowActionsMenu(!showActionsMenu)
  }

  const handleUnstar = async () => {
    try {
      if (client) {
        await client.unpinMessage(message)
        await onUnstar(message.id)
      }
    } catch (error) {
      console.error('Error unstarring message:', error)
    }
    setShowActionsMenu(false)
  }

  const messageClasses = isMyMessage()
    ? 'str-chat__message str-chat__message--me str-chat__message-simple str-chat__message-simple--me'
    : 'str-chat__message str-chat__message-simple'

  return (
    <div 
      className={`${messageClasses}`}
      ref={messageRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="mt-2 w-full relative">
        <div className={`flex gap-3 ${isMyMessage() ? 'justify-end' : 'justify-start'}`}>
          {!isMyMessage() && (
            <AppAvatar
              size="md"
              type="mixed"
              shape="Rounded"
              text={getInitials({
                firstName: message.user?.firstName as string,
                lastName: message.user?.lastName as string,
              })}
            />
          )}
          <div
            className={`flex flex-col max-w-[75%] overflow-visible ${
              isMyMessage() ? 'items-end' : 'items-start'
            }`}
          >
            <div
              className={`text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1 ${
                isMyMessage() ? 'mr-1 justify-end' : 'ml-1 justify-start'
              }`}
            >
              <span>
                {isMyMessage()
                  ? 'You'
                  : `${message.user?.firstName} ${message.user?.lastName}`}
              </span>
              {message.pinned && (
                <div 
                  className="text-yellow-500" 
                  title="Starred message"
                >
                  <StarIcon filled={true} />
                </div>
              )}
            </div>
            <div className="relative">
              <div
                className={`rounded-2xl ${
                  isMyMessage() ? 'bg-app-primary-10 text-white' : 'bg-app-grey-5'
                }`}
              >
                <StarredMessageText message={message} />
              </div>
              
              {/* Three dots menu button - appears on hover */}
              {isHovering && (
                <div className={`absolute bottom-1 ${isMyMessage() ? 'left-[-30px]' : 'right-[-30px]'}`}>
                  <button
                    onClick={handleThreeDotsClick}
                    className="p-1.5 rounded-full bg-white shadow-sm hover:bg-gray-50 transition-colors border border-gray-200 opacity-90 hover:opacity-100"
                    title="More options"
                  >
                    <ThreeDotsIcon />
                  </button>
                  
                  {/* Actions dropdown menu */}
                  <StarredMessageActionsMenu
                    isVisible={showActionsMenu}
                    onUnstar={handleUnstar}
                    message={message}
                  />
                </div>
              )}
            </div>

            <div className="text-xs text-silver-darker mt-1 px-1 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>
                  {new Date(message.created_at).toLocaleDateString()} at{' '}
                  {new Date(message.created_at).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
            </div>
          </div>
          {isMyMessage() && (
            <AppAvatar
              size="md"
              type="mixed"
              shape="Rounded"
              text={getInitials({
                firstName: message.user?.firstName as string,
                lastName: message.user?.lastName as string,
              })}
            />
          )}
        </div>
      </div>
    </div>
  )
}
